import * as React from "react";
import * as moment from "moment";
import { observer, inject } from "mobx-react";
import BootstrapTable from "react-bootstrap-table-next";
import { UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";

import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "../../stores/CustomerStore";

import { AgentAndDelegatorAuthorization, ExtractFriendlyName } from "../../model/Customer";
import { AttachmentFile } from "src/model/Attachment";

import { RevokeAgentAuthorizationRequest } from "src/requests/CustomerRequests";

import { TextareaGroup, SelectGroup } from "../common/SimpleFormComponents";
import { successMessage, errorMessage } from "../common/Toast";
import { CustomerLink } from "../common/CustomerLink";
import { FormDialog } from "../common/FormDialog";

import * as fileUtil from "../../common/Methods/FileUtil";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEdit } from "@fortawesome/free-solid-svg-icons";
import LoadingWidget from "../common/LoadingWidget";

interface RevokeAgentAuthorizationEditableFields {
    authorizationId: number | null;
    notes?: string;
}

const EmptyRevokeAgentAuthorization = (): RevokeAgentAuthorizationEditableFields => {
    return {
        authorizationId: null,
        notes: ""
    };
};

export interface Props {
    configurationStore?: ConfigurationStore;
    customerStore?: CustomerStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;

    title: string;
    data: Array<AgentAndDelegatorAuthorization>;
    editFields?: (row: AgentAndDelegatorAuthorization) => void;
    agentFor?: boolean;
}

export interface StatePage {
    showRevokeDialog: boolean;
    wasValidated: boolean;
}

export interface StateForm {
    selectedSaleId: number | null;
}

type State = StatePage & StateForm & RevokeAgentAuthorizationEditableFields;

@inject("configurationStore")
@inject("attachmentLogEndpoints")
@inject("customerStore")
@observer
export default class AgentAutorizationListComponent extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            ...EmptyRevokeAgentAuthorization(),
            showRevokeDialog: false,
            wasValidated: false,
            selectedSaleId: null
        };
    }

    revokeAgentAuthorization = async () => {
        try {
            const request: RevokeAgentAuthorizationRequest = {
                effectiveDate: new Date().getTime(),
                notes: this.state.notes || ""
            };
            if (this.state.authorizationId) {
                await this.props.customerStore!.revokeAgentAuthorization(this.state.authorizationId, request);
                this.closeRevokeDialog();
                successMessage(`Agent Authorization has been revoked.`);
            }
        } catch (error) {
            errorMessage(error);
        }
    };

    getAttachment = (file: AttachmentFile) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        try {
            const blob = await this.props.attachmentLogEndpoints!.getAttachment(file.id);
            fileUtil.DownloadBlob(file.name, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.form && this.form.checkValidity()) {
            this.setState({ wasValidated: false });
            this.revokeAgentAuthorization();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    closeRevokeDialog = () => {
        this.setState({
            ...EmptyRevokeAgentAuthorization(),
            showRevokeDialog: false,
            wasValidated: false
        });
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        if (!customerStore.customer) {
            return <div />;
        }
        const creditApplications = customerStore.customer!.creditApplications;
        if (!creditApplications) {
            return <div />;
        }

        const filterData = this.props.data
            .filter(hfs => this.state.selectedSaleId === null || hfs.sale.id === this.state.selectedSaleId)
            .sort((a, b) => b.dateCreated - a.dateCreated);

        const columns = [
            {
                dataField: "sale",
                text: "Sale",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    return row.sale.name;
                }
            },
            {
                dataField: "agent",
                text: `${this.props.agentFor ? `Authorized By` : `Agent Name`}`,
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    return this.props.agentFor ? (
                        <CustomerLink customer={row.delegator} targetBlank={true} />
                    ) : (
                        <CustomerLink customer={row.agent} targetBlank={true} />
                    );
                }
            },
            {
                dataField: "address",
                text: "Address",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    let address = " - ";
                    if (this.props.agentFor) {
                        if (row.delegator.address) {
                            address =
                                (row.delegator.address.address ? row.delegator.address.address + ", " : "") +
                                (row.delegator.address.city ? row.delegator.address.city + ", " : "") +
                                (row.delegator.address.province ? row.delegator.address.province.name + " " : "") +
                                (row.delegator.address.postalCode ? " " + row.delegator.address.postalCode : "") +
                                (row.delegator.address.country ? " " + row.delegator.address.country.name : "");
                        }
                    } else {
                        if (row.agent.address) {
                            address =
                                (row.agent.address.address ? row.agent.address.address + ", " : "") +
                                (row.agent.address.city ? row.agent.address.city + ", " : "") +
                                (row.agent.address.province ? row.agent.address.province.name + " " : "") +
                                (row.agent.address.postalCode ? " " + row.agent.address.postalCode : "") +
                                (row.agent.address.country ? " " + row.agent.address.country.name : "");
                        }
                    }
                    return address;
                }
            },
            {
                dataField: "email",
                text: "Email",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    let email = " - ";
                    if (this.props.agentFor) {
                        email = row.delegator.email ? row.delegator.email : " - ";
                    } else {
                        email = row.agent.email ? row.agent.email : " - ";
                    }
                    return email;
                }
            },
            {
                dataField: "phone",
                text: "Phone",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    let primaryPhone = " - ";
                    if (this.props.agentFor) {
                        primaryPhone = row.delegator.primaryPhone ? row.delegator.primaryPhone : " - ";
                    } else {
                        primaryPhone = row.agent.primaryPhone ? row.agent.primaryPhone : " - ";
                    }
                    return primaryPhone;
                }
            },
            {
                dataField: "dateSigned",
                text: "Date Signed",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    return row.dateSigned ? moment(row.dateSigned).format("YYYY-MM-DD") : " - ";
                }
            },
            {
                dataField: "notes",
                text: "Notes"
            },
            {
                dataField: "state",
                text: "Status",
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    // ! CHECK NEW DESIGN ON THE EFFECTIVE DATE
                    return row.dateRevoked === null ? (
                        "Active"
                    ) : (
                        <>
                            Revoked
                            {row.revokedNotes !== null && row.revokedNotes !== "" ? (
                                <>
                                    <span id={`tooltipRevokeNotes${row.id}`} className="badge badge-info ml-2">
                                        Notes
                                    </span>
                                    <UncontrolledTooltip placement="top" target={`tooltipRevokeNotes${row.id}`} delay={0} className="tooltipstrap">
                                        {row.revokedNotes}
                                    </UncontrolledTooltip>
                                </>
                            ) : (
                                ""
                            )}
                        </>
                    );
                }
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "180px", textAlign: "center" },
                formatter: (cellContent: any, row: AgentAndDelegatorAuthorization) => {
                    return (
                        <>
                            {row.attachmentLog.files.length > 0 && (
                                <>
                                    <button
                                        className="btn btn-secondary btn-sm mr-1"
                                        onClick={this.getAttachment(row.attachmentLog.files[0])}
                                        id={`tooltipDownload${row.id}`}
                                    >
                                        <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                    <UncontrolledTooltip placement="top" target={`tooltipDownload${row.id}`} delay={0} className="tooltipstrap">
                                        Download {row.attachmentLog.files[0].name}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.dateRevoked === null && !this.props.agentFor && this.props.editFields && (
                                <>
                                    <button
                                        className="btn btn-secondary btn-sm mr-1"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (this.props.editFields) {
                                                this.props.editFields(row);
                                            }
                                            window.scrollTo(0, 0);
                                        }}
                                        id={`tooltipEdit${row.id}`}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                        Edit Agent Authorization
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.dateRevoked === null && (
                                <div className="btn-group btn-group-toggle mr-1" data-toggle="buttons">
                                    <button
                                        className="btn btn-info btn-sm"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            this.setState({ authorizationId: row.id, showRevokeDialog: true });
                                        }}
                                    >
                                        Revoke
                                    </button>
                                </div>
                            )}
                            {this.props.agentFor && (
                                <Link
                                    className="text-wrap mt-1 py-2 d-block"
                                    to={`/customer/${row.delegator.id}/agent-authorization`}
                                    style={{ lineHeight: "1rem" }}
                                >
                                    <small>Go to {ExtractFriendlyName(row.delegator)} Agent Authorization Page</small>
                                </Link>
                            )}
                        </>
                    );
                }
            }
        ];

        return (
            <>
                <div className="card mb-3">
                    <div className="card-header">{this.props.title}</div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-12">
                                <SelectGroup
                                    label="Sale:"
                                    name="documentType"
                                    isRequired={false}
                                    className={`col-md-12 mx-0 px-0`}
                                    disabled={false}
                                    value={this.state.selectedSaleId || ""}
                                    onChange={e => {
                                        e.preventDefault();
                                        const selectedSaleId = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                        this.setState({ selectedSaleId });
                                    }}
                                >
                                    <option value="">Select sale...</option>
                                    {configurationStore!.horseSales
                                        .slice()
                                        .sort((a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                        .map((c: any, i: any) => (
                                            <option key={i} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                </SelectGroup>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <BootstrapTable
                                keyField="id"
                                remote={true}
                                hover={true}
                                bordered={false}
                                className="table-sm"
                                columns={columns}
                                bootstrap4={true}
                                data={filterData}
                                noDataIndication={() =>
                                    customerStore.loading ? (
                                        <LoadingWidget />
                                    ) : (
                                        <div>
                                            <span>No results found.</span>
                                        </div>
                                    )
                                }
                            />
                            {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                Otherwise the `noDataIndication` wouldn't work */}
                            {customerStore.loading && <div />}
                        </div>
                    </div>
                </div>

                <FormDialog
                    title="Revoke Authorization"
                    modalSize="modal-md"
                    show={this.state.showRevokeDialog}
                    onClose={() => this.closeRevokeDialog()}
                >
                    <form
                        className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                        noValidate={true}
                        onSubmit={this.submit}
                        ref={ref => (this.form = ref)}
                    >
                        <div className="form-row">
                            <TextareaGroup
                                label="Notes"
                                rows={4}
                                isRequired={false}
                                name="notes"
                                className="form-group col-md-12"
                                value={this.state.notes}
                                onChange={e => this.setState({ notes: e.target.value })}
                            />
                        </div>
                        <div className="form-row text-right">
                            <button type="button" className="btn btn-secondary mr-auto" onClick={() => this.closeRevokeDialog()}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Revoke
                            </button>
                        </div>
                    </form>
                </FormDialog>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/AgentAutorizationListComponent.tsx